import { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { publicAPI } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Loader2, CheckCircle, Phone, Building2 } from 'lucide-react';
import { AiEstimationWizard } from '@/components/AiEstimationWizard';

export function PublicFormPage() {
  const { formSlug } = useParams();
  const [form, setForm] = useState(null);
  const [workspace, setWorkspace] = useState(null);
  const [aiPreset, setAiPreset] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [priceRange, setPriceRange] = useState(null);
  const [formData, setFormData] = useState({});
  const [errors, setErrors] = useState({});

  // --- Iframe auto-height: tell the parent page (WordPress) our height ---
  useEffect(() => {
    if (window.parent === window) return undefined;
    const measure = () => {
      // The page wrapper is min-h-screen (= iframe height), so measure its
      // children instead — lets the parent shrink the iframe too.
      const outer = document.querySelector('.min-h-screen');
      if (!outer || outer.children.length === 0) return Math.ceil(document.documentElement.scrollHeight);
      const cs = window.getComputedStyle(outer);
      const padBottom = parseFloat(cs.paddingBottom) || 0;
      const top = outer.getBoundingClientRect().top;
      let bottom = 0;
      for (const child of outer.children) {
        bottom = Math.max(bottom, child.getBoundingClientRect().bottom - top);
      }
      return Math.ceil(bottom + padBottom);
    };
    let last = 0;
    const post = () => {
      const h = measure();
      if (!h || h === last) return;
      last = h;
      window.parent.postMessage({ type: 'spaceleadx:resize', slug: formSlug, height: h }, '*');
    };
    post();
    const ro = new ResizeObserver(post);
    ro.observe(document.getElementById('root') || document.body);
    const id = setInterval(post, 800);
    return () => { ro.disconnect(); clearInterval(id); };
  }, [formSlug]);

  // --- Wizard funnel tracking (AI estimation forms only, fully silent) ---
  const wizardSessionIdRef = useRef(null);
  const getWizardSessionId = () => {
    if (wizardSessionIdRef.current) return wizardSessionIdRef.current;
    let sid = null;
    try {
      const key = `slx_wizard_session_${formSlug}`;
      sid = sessionStorage.getItem(key);
      if (!sid) {
        sid = window.crypto?.randomUUID?.() || `${Date.now()}-${Math.random().toString(36).slice(2)}`;
        sessionStorage.setItem(key, sid);
      }
    } catch {
      sid = `${Date.now()}-${Math.random().toString(36).slice(2)}`;
    }
    wizardSessionIdRef.current = sid;
    return sid;
  };
  const trackWizard = (eventType, extra = {}) => {
    try {
      publicAPI
        .trackWizardEvent({
          form_slug: formSlug,
          session_id: getWizardSessionId(),
          event_type: eventType,
          ...extra,
        })
        .catch(() => {}); // tracking failures must never affect the form
    } catch { /* silent */ }
  };

  useEffect(() => {
    loadForm();
  }, [formSlug]);

  const loadForm = async () => {
    try {
      const response = await publicAPI.getForm(formSlug);
      setForm(response.data.form);
      setWorkspace(response.data.workspace);
      setAiPreset(response.data.ai_preset || null);

      // Initialize form data
      const initialData = {};
      response.data.form.fields?.forEach(field => {
        if (field.type === 'checkbox') {
          initialData[field.field_id] = false;
        } else {
          initialData[field.field_id] = '';
        }
      });
      setFormData(initialData);
    } catch (err) {
      console.error('Error loading form:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const getFieldValue = (fieldId) => {
    return formData[fieldId] || '';
  };

  const setFieldValue = (fieldId, value) => {
    setFormData(prev => ({ ...prev, [fieldId]: value }));
    // Clear error when user types
    if (errors[fieldId]) {
      setErrors(prev => ({ ...prev, [fieldId]: null }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    form.fields?.forEach(field => {
      if (field.required) {
        const value = formData[field.field_id];
        if (!value || (typeof value === 'string' && !value.trim())) {
          newErrors[field.field_id] = 'Ce champ est requis';
        }
      }
      
      // Email validation
      if (field.type === 'email' && formData[field.field_id]) {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(formData[field.field_id])) {
          newErrors[field.field_id] = 'Email invalide';
        }
      }
    });
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;
    
    setIsSubmitting(true);
    
    try {
      // Build submission data using labels as keys
      const submissionData = {};
      form.fields?.forEach(field => {
        const key = field.crm_mapping || field.label.toLowerCase().replace(/\s+/g, '_');
        submissionData[key] = formData[field.field_id];
      });
      
      // Add tracking data
      const tracking = {
        utm_source: new URLSearchParams(window.location.search).get('utm_source'),
        utm_medium: new URLSearchParams(window.location.search).get('utm_medium'),
        utm_campaign: new URLSearchParams(window.location.search).get('utm_campaign'),
        gclid: new URLSearchParams(window.location.search).get('gclid'),
        referrer: document.referrer,
        landing_page: window.location.href,
      };
      
      const response = await publicAPI.submitForm(formSlug, submissionData, tracking);
      setSuccessMessage(response.data.message);
      setIsSubmitted(true);
    } catch (err) {
      console.error('Error submitting form:', err);
      alert(err.response?.data?.detail || 'Erreur lors de l\'envoi');
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderField = (field) => {
    const value = getFieldValue(field.field_id);
    const error = errors[field.field_id];
    
    const commonProps = {
      id: field.field_id,
      className: `h-12 ${error ? 'border-destructive' : ''}`,
    };

    switch (field.type) {
      case 'text':
      case 'email':
      case 'phone':
      case 'number':
      case 'date':
        return (
          <Input
            {...commonProps}
            type={field.type === 'phone' ? 'tel' : field.type}
            placeholder={field.placeholder}
            value={value}
            onChange={(e) => setFieldValue(field.field_id, e.target.value)}
            data-testid={`field-${field.field_id}`}
          />
        );
      
      case 'textarea':
        return (
          <Textarea
            id={field.field_id}
            placeholder={field.placeholder}
            value={value}
            onChange={(e) => setFieldValue(field.field_id, e.target.value)}
            className={`min-h-[100px] ${error ? 'border-destructive' : ''}`}
            data-testid={`field-${field.field_id}`}
          />
        );
      
      case 'select':
        return (
          <Select 
            value={value} 
            onValueChange={(v) => setFieldValue(field.field_id, v)}
          >
            <SelectTrigger className={`h-12 ${error ? 'border-destructive' : ''}`} data-testid={`field-${field.field_id}`}>
              <SelectValue placeholder={field.placeholder || 'Sélectionner...'} />
            </SelectTrigger>
            <SelectContent>
              {field.options?.map((option, i) => (
                <SelectItem key={i} value={option}>{option}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        );
      
      case 'checkbox':
        return (
          <div className="flex items-center gap-3">
            <Checkbox
              id={field.field_id}
              checked={!!value}
              onCheckedChange={(checked) => setFieldValue(field.field_id, checked)}
              data-testid={`field-${field.field_id}`}
            />
            <Label htmlFor={field.field_id} className="font-normal cursor-pointer">
              {field.placeholder || field.label}
            </Label>
          </div>
        );
      
      case 'radio':
        return (
          <RadioGroup 
            value={value} 
            onValueChange={(v) => setFieldValue(field.field_id, v)}
            data-testid={`field-${field.field_id}`}
          >
            {field.options?.map((option, i) => (
              <div key={i} className="flex items-center gap-3">
                <RadioGroupItem value={option} id={`${field.field_id}-${i}`} />
                <Label htmlFor={`${field.field_id}-${i}`} className="font-normal cursor-pointer">
                  {option}
                </Label>
              </div>
            ))}
          </RadioGroup>
        );
      
      default:
        return (
          <Input
            {...commonProps}
            type="text"
            placeholder={field.placeholder}
            value={value}
            onChange={(e) => setFieldValue(field.field_id, e.target.value)}
          />
        );
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-secondary to-background">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!form) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-secondary to-background p-4">
        <Card className="max-w-md w-full">
          <CardContent className="py-12 text-center">
            <p className="text-lg font-medium">Formulaire non trouvé</p>
            <p className="text-muted-foreground mt-2">Ce formulaire n'existe pas ou a été désactivé.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (isSubmitted) {
    const phone = form?.emergency_phone || workspace?.branding?.phone;
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-secondary to-background p-4">
        <Card className="max-w-md w-full">
          <CardContent className="py-10 text-center space-y-4">
            <CheckCircle className="h-16 w-16 text-green-500 mx-auto" />
            <h2 className="text-2xl font-bold">Merci!</h2>
            <p className="text-muted-foreground whitespace-pre-line text-left">{successMessage}</p>

            {priceRange && (
              <div className="text-left space-y-3" data-testid="wizard-result-block">
                {priceRange.urgent && (
                  <div className="rounded-lg border-2 border-red-500 bg-red-50 p-4" data-testid="result-urgent-alert">
                    <p className="font-bold text-red-700">
                      🚨 Infiltration d'eau active — intervention urgente recommandée
                    </p>
                    <p className="text-sm text-red-700/90 mt-1">
                      Communiquez immédiatement avec notre équipe par téléphone pour une évaluation prioritaire.
                    </p>
                    {phone && (
                      <a
                        href={`tel:${String(phone).replace(/[^+\d]/g, '')}`}
                        className="mt-3 inline-flex items-center gap-2 px-4 py-2.5 rounded-lg bg-red-600 hover:bg-red-700 text-white font-bold text-sm"
                        data-testid="result-urgent-call-btn"
                      >
                        <Phone className="h-4 w-4" />
                        Appeler maintenant — {phone}
                      </a>
                    )}
                  </div>
                )}

                {priceRange.min != null && priceRange.max != null ? (
                  <div className="rounded-lg border-2 border-green-500/40 bg-green-50 p-4" data-testid="result-price-range">
                    <p className="text-xs uppercase tracking-wide text-green-700 font-semibold mb-1">
                      Estimation préliminaire
                    </p>
                    <p className="text-2xl font-black text-green-800 tabular-nums">
                      {priceRange.min.toLocaleString('fr-CA')} $ – {priceRange.max.toLocaleString('fr-CA')} $
                    </p>
                    <p className="text-xs text-green-700 mt-0.5">CAD, taxes exclues</p>
                    <p className="text-xs text-green-800 mt-1 font-medium" data-testid="result-subject-to-validation">
                      Sujet à validation par notre équipe.
                    </p>
                  </div>
                ) : !priceRange.urgent && priceRange.display_label ? (
                  <div className="rounded-lg border bg-amber-50 border-amber-300 p-4" data-testid="result-no-price">
                    <p className="font-semibold text-amber-800">{priceRange.display_label}</p>
                  </div>
                ) : null}

                {priceRange.requires_validation && !priceRange.urgent && (
                  <p className="text-sm text-amber-700 font-medium" data-testid="result-validation-required">
                    ⚠️ Une validation par notre équipe est requise avant de confirmer le prix final.
                  </p>
                )}

                {(priceRange.notes || []).length > 0 && (
                  <ul className="text-sm text-muted-foreground space-y-1.5 list-disc pl-5" data-testid="result-notes">
                    {priceRange.notes.map((n, i) => (
                      <li key={i}>{n}</li>
                    ))}
                  </ul>
                )}

                <p className="text-xs text-muted-foreground border-t pt-3" data-testid="result-disclaimer">
                  {priceRange.disclaimer ||
                    "Estimation indicative avant taxes. Aucun prix n'est final. Le montant peut varier selon les conditions réelles du chantier, la localisation, l'accessibilité, la profondeur, les obstacles et la remise en état du terrain. Toute estimation demeure sujette à l'analyse et à l'approbation de notre équipe. Cette estimation ne constitue pas une soumission officielle."}
                </p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    );
  }

  const primaryColor = workspace?.branding?.primary_color || '#F59E0B';

  // ------------------------------------------------------------------
  // Appearance / custom theme resolver
  // ------------------------------------------------------------------
  // The admin UI saves the public-form appearance under various keys
  // depending on legacy ages of the codebase. Rather than guessing one,
  // we walk a list of candidate sources in priority order and normalise
  // whatever structure we find into the snake_case shape that the CSS
  // block below already understands.
  //
  // Accepted shapes (camelCase and snake_case both tolerated):
  //   pageBackground       | page_background       | background_color
  //   cardBackground       | card_background       | card_background_color
  //   primaryColor         | primary_color
  //   buttonTextColor      | button_text_color
  //   textColor            | text_color
  //   inputBackground      | input_background      | input_background_color
  //   inputBorder          | input_border          | input_border_color
  //   enabled (bool)       — if explicitly false, customization is OFF.
  //   mode (string)        — informational only, e.g. "light".
  //
  // If NO source is found OR `enabled === false`, we return null and the
  // default SpaceLeadX styling kicks in unchanged.
  const resolveAppearance = () => {
    const candidates = [
      form?.custom,
      form?.appearance,
      form?.theme,
      workspace?.public_form_appearance,
      workspace?.appearance,
      workspace?.custom,
    ].filter(Boolean);

    for (const raw of candidates) {
      if (!raw || typeof raw !== 'object') continue;
      // Explicit opt-out wins immediately.
      if (raw.enabled === false) return null;
      const pick = (...keys) => {
        for (const k of keys) {
          if (raw[k] !== undefined && raw[k] !== null && raw[k] !== '') return raw[k];
        }
        return undefined;
      };
      const norm = {
        background_color: pick('pageBackground', 'page_background', 'background_color'),
        card_background_color: pick('cardBackground', 'card_background', 'card_background_color'),
        primary_color: pick('primaryColor', 'primary_color'),
        button_text_color: pick('buttonTextColor', 'button_text_color'),
        text_color: pick('textColor', 'text_color'),
        input_background_color: pick('inputBackground', 'input_background', 'input_background_color'),
        input_border_color: pick('inputBorder', 'input_border', 'input_border_color'),
        mode: pick('mode'),
      };
      // Consider customization "active" if at least one colour is set OR
      // `enabled === true` was passed explicitly.
      const hasAnyColor = Object.entries(norm).some(([k, v]) => k !== 'mode' && !!v);
      if (raw.enabled === true || hasAnyColor) return norm;
    }
    return null;
  };

  // Theme support — preserves prior behaviour: any non-null resolved theme
  // overrides the default dark/gradient surfaces with custom colours.
  const theme = resolveAppearance();
  const themeBtnColor = theme?.primary_color || primaryColor;
  const themeId = `theme-${form.form_id || form.slug || 'pub'}`;

  // When a theme is active, inject a scoped <style> block. We use !important
  // because Shadcn UI components (Card, Input, Textarea, Select) ship their
  // own background/foreground utilities (bg-card, bg-background, text-foreground,
  // text-muted-foreground, border) that have equal or higher CSS specificity
  // than inline styles applied on parent wrappers — particularly for nested
  // children. The selectors below cover every public-form surface:
  //   - outer wrapper + business header
  //   - card + header divider
  //   - labels + descriptions + placeholders
  //   - inputs / textareas / selects / radios / checkboxes
  //   - the submit button
  const scopedCss = theme ? `
    [data-form-theme="${themeId}"] { background-color: ${theme.background_color || '#FFFFFF'} !important; color: ${theme.text_color || '#0F172A'} !important; }
    [data-form-theme="${themeId}"] h1,
    [data-form-theme="${themeId}"] h2,
    [data-form-theme="${themeId}"] h3,
    [data-form-theme="${themeId}"] p,
    [data-form-theme="${themeId}"] label,
    [data-form-theme="${themeId}"] a { color: ${theme.text_color || '#0F172A'} !important; }
    [data-form-theme="${themeId}"] .text-muted-foreground { color: ${theme.text_color || '#0F172A'} !important; opacity: 0.7; }
    [data-form-theme="${themeId}"] [data-slot="card"],
    [data-form-theme="${themeId}"] .form-card { background-color: ${theme.card_background_color || '#FFFFFF'} !important; color: ${theme.text_color || '#0F172A'} !important; border-color: ${theme.input_border_color || 'rgba(0,0,0,0.08)'} !important; }
    [data-form-theme="${themeId}"] .border-b,
    [data-form-theme="${themeId}"] .border-t,
    [data-form-theme="${themeId}"] [data-slot="separator"] { border-color: ${theme.input_border_color || 'rgba(0,0,0,0.08)'} !important; }
    [data-form-theme="${themeId}"] input,
    [data-form-theme="${themeId}"] textarea,
    [data-form-theme="${themeId}"] [role="combobox"],
    [data-form-theme="${themeId}"] [data-slot="select-trigger"] { background-color: ${theme.input_background_color || '#FFFFFF'} !important; color: ${theme.text_color || '#0F172A'} !important; border-color: ${theme.input_border_color || '#D6D3D1'} !important; }
    [data-form-theme="${themeId}"] input::placeholder,
    [data-form-theme="${themeId}"] textarea::placeholder { color: ${theme.text_color || '#0F172A'} !important; opacity: 0.45; }
    [data-form-theme="${themeId}"] button[type="submit"] { background-color: ${themeBtnColor} !important; color: ${theme.button_text_color || '#FFFFFF'} !important; border-color: ${themeBtnColor} !important; }
    [data-form-theme="${themeId}"] [role="radio"],
    [data-form-theme="${themeId}"] [data-slot="checkbox"],
    [data-form-theme="${themeId}"] [data-slot="radio-group-item"] { border-color: ${theme.input_border_color || '#D6D3D1'} !important; background-color: ${theme.input_background_color || '#FFFFFF'} !important; }
    [data-form-theme="${themeId}"] [role="radio"][data-state="checked"],
    [data-form-theme="${themeId}"] [data-slot="checkbox"][data-state="checked"] { background-color: ${themeBtnColor} !important; border-color: ${themeBtnColor} !important; color: ${theme.button_text_color || '#FFFFFF'} !important; }
  ` : '';

  return (
    <div
      className={theme ? 'min-h-screen p-4 py-8' : 'min-h-screen bg-gradient-to-br from-secondary/30 to-background p-4 py-8'}
      data-form-theme={theme ? themeId : undefined}
      data-theme-mode={theme?.mode || 'default'}
    >
      {theme && <style dangerouslySetInnerHTML={{ __html: scopedCss }} />}
      <div className="max-w-lg mx-auto">
        {/* Header */}
        {workspace && (
          <div className="text-center mb-6">
            <div className="flex items-center justify-center gap-2 mb-2">
              <Building2 className="h-6 w-6" style={{ color: themeBtnColor }} />
              <h1 className="font-heading text-2xl font-black">
                {workspace.branding?.company_name || workspace.name}
              </h1>
            </div>
            {workspace.branding?.phone && (
              <a 
                href={`tel:${workspace.branding.phone}`}
                className="text-sm text-muted-foreground hover:text-primary flex items-center justify-center gap-1"
              >
                <Phone className="h-4 w-4" />
                {workspace.branding.phone}
              </a>
            )}
          </div>
        )}

        {/* Form Card */}
        {form.form_type === 'ai_estimation' && aiPreset ? (
          <AiEstimationWizard
            preset={aiPreset}
            themeBtnColor={themeBtnColor}
            onTrack={trackWizard}
            workspacePhone={form?.emergency_phone || workspace?.branding?.phone}
            onSubmit={async ({ answers, photos, honeypot }) => {
              const tracking = {
                utm_source: new URLSearchParams(window.location.search).get('utm_source'),
                utm_medium: new URLSearchParams(window.location.search).get('utm_medium'),
                utm_campaign: new URLSearchParams(window.location.search).get('utm_campaign'),
                gclid: new URLSearchParams(window.location.search).get('gclid'),
                referrer: document.referrer,
                landing_page: window.location.href,
              };
              try {
                const resp = await publicAPI.submitAiEstimation(formSlug, { answers, photos, tracking, honeypot });
                trackWizard('wizard_completed');
                setPriceRange(resp.data.price_range || null);
                setSuccessMessage(resp.data.message || form.success_message || 'Merci !');
                setIsSubmitted(true);
              } catch (err) {
                console.error('Error submitting AI estimation:', err);
                alert(err.response?.data?.detail || "Erreur lors de l'envoi. Réessayez svp.");
                throw err;
              }
            }}
          />
        ) : (
        <Card className="shadow-xl form-card">
          <CardHeader className="text-center border-b">
            <CardTitle className="text-xl">{form.name}</CardTitle>
            {form.description && (
              <p className="text-sm text-muted-foreground mt-1">{form.description}</p>
            )}
          </CardHeader>
          <CardContent className="pt-6">
            <form onSubmit={handleSubmit} className="space-y-5">
              {/* Honeypot field */}
              <input 
                type="text" 
                name="_honeypot" 
                style={{ display: 'none' }}
                tabIndex={-1}
                autoComplete="off"
                value={formData._honeypot || ''}
                onChange={(e) => setFieldValue('_honeypot', e.target.value)}
              />

              {form.fields?.sort((a, b) => a.order - b.order).map((field) => (
                <div key={field.field_id} className="space-y-2">
                  {field.type !== 'checkbox' && (
                    <Label htmlFor={field.field_id} className="flex items-center gap-1">
                      {field.label}
                      {field.required && <span className="text-destructive">*</span>}
                    </Label>
                  )}
                  {renderField(field)}
                  {errors[field.field_id] && (
                    <p className="text-sm text-destructive">{errors[field.field_id]}</p>
                  )}
                </div>
              ))}

              <Button
                type="submit"
                className="w-full h-12 font-bold text-base"
                style={!theme ? { backgroundColor: primaryColor } : undefined}
                disabled={isSubmitting}
                data-testid="submit-form-btn"
              >
                {isSubmitting ? (
                  <Loader2 className="h-5 w-5 animate-spin" />
                ) : (
                  'Envoyer ma demande'
                )}
              </Button>
            </form>
          </CardContent>
        </Card>
        )}
      </div>
    </div>
  );
}
